import React, { useState, useEffect } from "react";
import {
  Table,
  Pagination,
  Button,
  FormControl,
  Modal,
  Form,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import { FaBackward } from "react-icons/fa";
import { getBindingDisputeTrack } from "./services/bindingDisputeService";
import axios from "axios";
import { getURLbyEndPoint } from "../../../../../store/api";
import { getAuthUserlocal } from "../../../../../store/services";
import { bankNames } from "../../../../auth/Register/RegisterPage3";
import { PopUpModalButton } from "../../../../../components/basic/ModalComponent";
import BindingDisputeDetails from "./BindingDisputeDetails";
import BindingDisputeTimelineModal from "./BindingDisputeTimelineModal";

const BindingDisputeTrack = ({ setActivePage }) => {
  const [bindingTrack, setBindingTrack] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentData, setSelectedPaymentData] = useState(null);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [selectedTimelineData, setSelectedTimelineData] = useState(null);
  const [selectedDispute, setSelectedDispute] = useState(null); // State for selected dispute

  useEffect(() => {
    const fetchBindingDisputeTrack = async () => {
      try {
        const response = await getBindingDisputeTrack();
        if (response.status && Array.isArray(response.data)) {
          setBindingTrack(response.data);
        } else {
          setBindingTrack([]);
        }
      } catch (error) {
        setBindingTrack([]);
      }
    };
    fetchBindingDisputeTrack();
  }, []);

  // Filtering logic
  const filteredData = bindingTrack.filter((data) =>
    searchTerm !== "" && data.appNo
      ? data.appNo.toLowerCase().includes(searchTerm.toLowerCase())
      : true
  );

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const calculateRange = (currentPage, totalPages) => {
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (endPage - startPage < 4) {
      if (startPage === 1) {
        endPage = Math.min(5, totalPages);
      } else if (endPage === totalPages) {
        startPage = Math.max(totalPages - 4, 1);
      }
    }
    return { startPage, endPage };
  };

  const { startPage, endPage } = calculateRange(currentPage, totalPages);

  // Event Handlers
  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const handleClosePayment = () => setShowPaymentModal(false);

  const handleOpenPayment = (data) => {
    setSelectedPaymentData(data);
    setShowPaymentModal(true);
  };

  const handleCloseTimeline = () => setShowTimelineModal(false);

  const handleOpenTimeline = (data) => {
    setSelectedTimelineData(data);
    setShowTimelineModal(true);
  };

  const refreshDispute = async (disputeId) => {
    try {
      const response = await getBindingDisputeTrack();
      if (response.status && Array.isArray(response.data)) {
        const updatedDispute = response.data.find((d) => d._id === disputeId);
        if (updatedDispute) {
          setSelectedDispute(updatedDispute);
          setBindingTrack(response.data);
        }
      }
    } catch (error) {
      console.error("Error refreshing dispute:", error);
    }
  };

  const handleViewDetails = (data) => {
    setSelectedDispute(data); // Set selected dispute to show details
  };

  const handleBackToTrack = () => {
    setSelectedDispute(null); // Clear selected dispute to return to track list
  };

  return (
    <div>
      {selectedDispute ? (
        // Render BindingDisputeDetails when a dispute is selected
        <BindingDisputeDetails
          selectedData={selectedDispute}
          setActivePage={setActivePage}
          onBack={handleBackToTrack}
          onOpenPayment={handleOpenPayment}
          onRefresh={() => refreshDispute(selectedDispute._id)}
        />
      ) : (
        // Render Track Table when no dispute is selected
        <>
          <div className="d-flex gap-2 justify-content-end align-items-center">
            <FormControl
              className="form-control w-40"
              type="search"
              placeholder="Search By Application No."
              aria-label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button
              variant="secondary"
              onClick={() => setActivePage("homePage")}
            >
              <FaBackward /> Back
            </Button>
          </div>

          <div className="mt-3">
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>
                    Sr
                    <br />
                    No.
                  </th>
                  <th className="text-center">Application No</th>
                  <th className="text-center">Claimant</th>
                  <th className="text-center">Respondent</th>
                  <th className="text-center">Quantum of Claim</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((data, index) => (
                    <tr key={index}>
                      <td>{indexOfFirstItem + index + 1}.</td>
                      <td>
                        <div className="d-flex flex-column justify-content-center align-items-center">
                          {data.appNo}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column justify-content-center align-items-center">
                          {data.claimantName}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column justify-content-center align-items-center">
                          {data.respondentName}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column justify-content-center align-items-center">
                          $ {data.quantumOfClaim}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-2 align-items-center">
                          <span
                            className={`badge bg-${
                              data.status === "Pending"
                                ? "warning"
                                : data.status === "Approved"
                                ? "success"
                                : data.status === "Rejected"
                                ? "danger"
                                : "secondary"
                            }`}
                          >
                            {data.status}
                          </span>
                          {data.status === "Payment Link Sent" && (
                            <Button
                              variant="success"
                              disabled={data.status === "Payment in Process"}
                              size="sm"
                              onClick={() => handleOpenPayment(data)}
                            >
                              Make Payment
                            </Button>
                          )}
                          {data.timeTable?.sections[1]?.paymentDetails
                            ?.receipt &&
                            data.accessRole === "CLAIMANT" && (
                              <PopUpModalButton
                                title="Receipt"
                                fileLink={
                                  data.timeTable?.sections[1]?.paymentDetails
                                    ?.receipt
                                }
                                disabled={data.isApproved !== "Approved"}
                              />
                            )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column justify-content-center align-items-center gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleOpenTimeline(data)}
                          >
                            Case Timeline
                          </Button>
                          <Button
                            variant="info"
                            size="sm"
                            onClick={() => handleViewDetails(data)}
                          >
                            View Details
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center">
                      No Data Found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-start">
              <Pagination>
                <Pagination.Prev
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                />
                {Array.from({ length: endPage - startPage + 1 }, (_, index) => (
                  <Pagination.Item
                    key={index + startPage}
                    active={index + startPage === currentPage}
                    onClick={() => handlePageChange(index + startPage)}
                  >
                    {index + startPage}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <BindingPaymentProcessModal
          show={showPaymentModal}
          onHide={handleClosePayment}
          data={selectedPaymentData}
        />
      )}

      {/* Timeline Modal */}
      {showTimelineModal && (
        <BindingDisputeTimelineModal
          show={showTimelineModal}
          onHide={handleCloseTimeline}
          data={selectedTimelineData}
        />
      )}
    </div>
  );
};

// BindingPaymentProcessModal remains unchanged
function BindingPaymentProcessModal({ show, onHide, data }) {
  const [addAmount, setAddAmount] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const user = getAuthUserlocal();
  const walletBalance = user?.balance;
  const userId = user?._id;
  const amountRequired =
    data?.timeTable?.sections[1]?.paymentRequest.amountInINR;
  const exchangeRate =
    data?.timeTable?.sections[1]?.paymentRequest.exchangeRate;
  const requestedAt = data?.timeTable?.sections[1]?.paymentRequest.sentAt;

  const [formData, setFormData] = useState({
    utrNo: "",
    bankName: "",
    ifscCode: "",
    accountNumber: "",
    amount: amountRequired,
    paymentScreenShot: null,
  });

  useEffect(() => {
    setFormData((prev) => ({ ...prev, amount: amountRequired }));
  }, [amountRequired]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (file) => {
    if (file && file.size <= 3 * 1024 * 1024) {
      setFormData((prev) => ({ ...prev, paymentScreenShot: file }));
    } else {
      alert("File size exceeds 3MB. Please upload a smaller file.");
    }
  };

  const handleAddFund = async (e) => {
    e.preventDefault();

    const validationRules = {
      utrNo: {
        message: "Please enter a valid UTR Number.",
        isValid: () => formData.utrNo.trim() !== "",
      },
      bankName: {
        message: "Please select your bank name.",
        isValid: () => formData.bankName.trim() !== "",
      },
      accountNumber: {
        message: "Please enter your account number.",
        isValid: () => formData.accountNumber.trim() !== "",
      },
      ifscCode: {
        message: "Please enter a valid IFSC code.",
        isValid: () => formData.ifscCode.trim() !== "",
      },
      amount: {
        message: "Amount must be greater than 0.",
        isValid: () => formData.amount > 0,
      },
      paymentScreenShot: {
        message: "Please upload the payment screenshot.",
        isValid: () => formData.paymentScreenShot !== null,
      },
    };

    for (const key in validationRules) {
      const { message, isValid } = validationRules[key];
      if (!isValid()) {
        alert(message);
        return;
      }
    }

    setIsLoading(true);
    try {
      const disputePaymentInfo = {
        applicationNo: data.appNo,
        amount: amountRequired,
      };

      const apiFormData = new FormData();
      apiFormData.append("utrNo", formData.utrNo);
      apiFormData.append("amount", formData.amount);
      apiFormData.append("bankName", formData.bankName);
      apiFormData.append("ifscCode", formData.ifscCode);
      apiFormData.append("accountNumber", formData.accountNumber);
      apiFormData.append("paymentScreenShot", formData.paymentScreenShot);
      apiFormData.append("clientId", userId);
      apiFormData.append(
        "disputePaymentInfo",
        JSON.stringify(disputePaymentInfo)
      );

      const res = await axios.post(
        getURLbyEndPoint("addBindingDisputeFund") + userId,
        apiFormData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data && res.data.status) {
        alert(
          res.data.message ||
            "Funds added successfully! Your balance will be updated after verification."
        );
        window.location.reload();
      } else {
        alert(res.data.message || "Something went wrong!");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        "Server is not responding. Please try again later.";
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Make Payment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {data && (
          <>
            <h6 className="fw-bold mb-3">Dispute Summary</h6>
            <Table bordered size="sm" responsive className="mb-4">
              <tbody>
                <tr>
                  <td>
                    <strong>Application No</strong>
                  </td>
                  <td>{data.appNo || "N/A"}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Claimant</strong>
                  </td>
                  <td>{data.claimantName || "N/A"}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Amount (INR)</strong>
                  </td>
                  <td>₹ {amountRequired}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Exchange Rate</strong>
                  </td>
                  <td>{exchangeRate}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Requested At</strong>
                  </td>
                  <td>{new Date(requestedAt).toLocaleString()}</td>
                </tr>
              </tbody>
            </Table>
          </>
        )}

        <Card>
          <Card.Header>
            <Row>
              <Col md={7}>
                <h6 className="text-medium mb-1">
                  <strong>Company:</strong> {user.companyName}
                </h6>
                <h6 className="text-medium mb-0">
                  <strong>Membership No:</strong> {user.memberShipNo}
                </h6>
              </Col>
              <Col md={5}>
                <h6 className="text-medium mb-1">
                  <strong>Wallet Balance: ₹{walletBalance}</strong>
                </h6>
                <h6 className="text-medium mb-0">
                  <strong>Valid Upto:</strong> {user.validUpTo}
                </h6>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body>
            {!addAmount ? (
              <>
                <div className="ms-1 mb-3">
                  <div className="fw-semibold fs-6">
                    Debit Amount: ₹{amountRequired}
                  </div>
                </div>
                <Card className="p-2 mb-3 bg-light">
                  <Card.Text className="fs-6 mb-1 fw-bold">
                    Informative Note:
                  </Card.Text>
                  <ul className="mb-0" style={{ fontSize: "0.9rem" }}>
                    <li>
                      Initiate wallet payments only from the company's
                      registered and authorized bank account.
                    </li>
                    <li>
                      The beneficiary Account displayed on this platform is
                      unique to you only and should not be shared.
                    </li>
                    <li>
                      In Case of Rejection, Amount will be Credited to your
                      wallet.
                    </li>
                    <li>You can monitor Transaction entries in your wallet.</li>
                    <li>For Any Assistance, contact Chamber Desk.</li>
                  </ul>
                </Card>
                <div className="text-center mt-4">
                  {walletBalance < amountRequired ? (
                    <Button
                      variant="primary"
                      className="px-5"
                      onClick={() => setAddAmount(true)}
                    >
                      Add Balance
                    </Button>
                  ) : (
                    <Button
                      variant="success"
                      className="px-5"
                      onClick={() => window.location.assign("/wallet")}
                    >
                      Proceed to Payment
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <Form onSubmit={handleAddFund}>
                <h5 className="mb-3">Add Funds to Wallet</h5>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>UTR No.</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter UTR No."
                        value={formData.utrNo}
                        onChange={(e) =>
                          handleInputChange("utrNo", e.target.value)
                        }
                        maxLength={25}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Amount</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.amount}
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Bank Name</Form.Label>
                      <Form.Select
                        value={formData.bankName}
                        onChange={(e) =>
                          handleInputChange("bankName", e.target.value)
                        }
                        required
                      >
                        <option value="">Select a bank</option>
                        {bankNames.map((bank, idx) => (
                          <option key={idx} value={bank}>
                            {bank}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Account Number</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter account number"
                        value={formData.accountNumber}
                        onChange={(e) =>
                          handleInputChange("accountNumber", e.target.value)
                        }
                        maxLength={25}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>IFSC Code</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter IFSC code"
                        value={formData.ifscCode}
                        onChange={(e) =>
                          handleInputChange("ifscCode", e.target.value)
                        }
                        maxLength={25}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Payment Screenshot (max 3MB)</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e.target.files[0])}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <div className="text-center mt-4">
                  <Button
                    variant="success"
                    className="px-5"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? "Submitting..." : "Submit Request"}
                  </Button>
                </div>
              </Form>
            )}
          </Card.Body>
        </Card>
      </Modal.Body>
    </Modal>
  );
}

export default BindingDisputeTrack;
